import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import styles from '../login/styles/password-reset-form.scss';
import { Button, TextInput, Icon, Spinner, Snackbar } from 'shamrock-clover-ui';
import { useTranslation } from 'react-i18next';
import { LoginForms } from '../login/Login';
import { debounce, validateEmail } from '@shamrock-core/common/utils';
import { getLoginDetails } from '../../redux/actions/appActions';
import { useSelector } from 'react-redux';
import sentinelService from '../../services/sentinel.service';
import { TargetPayload } from './passwordResetScreens';
import { useSnackBarState } from './utils';

type PasswordResetFormProps = {
  backToLogin: () => void;
  setTarget: (target: TargetPayload) => void;
  setCurrentForm: (value: LoginForms) => void;
};

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ setTarget, backToLogin, setCurrentForm }) => {
  const { email } = useSelector(getLoginDetails);
  const [selection, setSelection] = useState<'sms' | 'email' | undefined>(undefined);
  const [snackBarProps, setSnackBarProps] = useSnackBarState();
  const [sendingLinkLoading, setSendingLinkLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(email);
  const [sendDisabled, setSendDisabled] = useState(true);
  const { t } = useTranslation();

  const sendResetLink = async () => {
    setSendingLinkLoading(true);
    try {
      const { phone } = await sentinelService.forgotPassword({ email, sendSms: selection === 'sms' });
      setTarget({
        target: selection,
        email,
        phone,
      });
      setCurrentForm(LoginForms.PASSWORD_RESET_SENT);
    } catch (error) {
      console.log('Temp logging of error:', error);
      setSnackBarProps({
        open: true,
        message: t('something_went_wrong_please_try_again_later'),
        variant: 'error',
      });
    } finally {
      setSendingLinkLoading(false);
    }
  };

  const checkError = useCallback(debounce(emailToTest => {
    const isEmailValid = emailToTest.length !== 0 && validateEmail(emailToTest);
    setSendDisabled(!isEmailValid);
  }, 200), []);

  useEffect(() => checkError(userEmail), []);

  const emailOnChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const trimmedEmail = value.trim();
    checkError(trimmedEmail);
    setUserEmail(trimmedEmail);
  };

  const radioChangeHandler = ({ target: { value } }) => setSelection(value);

  return (
    <div className={styles.contentBox}>
      <div className={styles.title}>
        <div className={styles.iconTitleContainer}>
          <Icon icon='chevronLeft' size="16" onClick={backToLogin} />
          <h2>{t('authentication.password_reset_title')}</h2>
        </div>
        <p>{t('authentication.where_would_you_like_to_receive_the_link_to_reset_your_password')}</p>
      </div>
      <div className={styles.radiosContainer}>
        <div className={styles.radioContainer}>
          <input
            className={styles.radioInput}
            checked={selection === 'sms'}
            type="radio"
            onChange={radioChangeHandler}
            id="sms"
            value="sms"
          />
          <label htmlFor="sms">{t('mobile_phone_number')}</label>
        </div>
        <div className={styles.radioContainer}>
          <input
            className={styles.radioInput}
            checked={selection === 'email'}
            type="radio"
            onChange={radioChangeHandler}
            id="email"
            value="email"
          />
          <label htmlFor="email">{t('email')}</label>
        </div>
      </div>
      <div>
        {selection && (
          <div>
            <p className={styles.emailInputText}>{t('authentication.enter_email_associated_with_account')}</p>
            <TextInput
              className={styles.inputRowTextInput}
              id="email"
              label={t('email')}
              variant="filled"
              inputType="text"
              value={userEmail}
              onChange={emailOnChange}
              error={sendDisabled && userEmail.length !== 0 && t('invalid_email')}
            />
          </div>
        )}
        <div className={styles.buttonContainer}>
          <div className={styles.button}>
            <Button buttonStyle={'outlined'} type="button" onClick={backToLogin} width={'164'} height={'44'}>
              <span className={styles.buttonText}>{t('back_to_login')}</span>
            </Button>
          </div>
          <div className={styles.button}>
            <Button
              disabled={sendDisabled || !selection}
              buttonStyle="solid"
              type="submit"
              width={'186'}
              height={'44'}
              onClick={sendResetLink}
            >
              {sendingLinkLoading ? <Spinner color="white" size="25" /> : t('authentication.password_reset_title')}
            </Button>
          </div>
        </div>
      </div>
      <Snackbar {...snackBarProps}/>
    </div>
  );
};
